import React, {
  useState,
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
} from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import dayjs from "dayjs";
import CommonSelect, { MenuOption, OptionGroup } from "../../components/commonSelect";
import {
  BulkVolunteer,
  bulkVolunteerMockData,
} from "../../helpers/bulkVolunteerMockData";
import { Avatar, Button, Typography } from "@mui/material";
import ComponentDivider from "../../components/componentDivider";
import ImportIcon from "../../static/images/import_icon.svg";
import {
  fetchAllEventsList,
  AllEventsResponse,
  Event as AllEventsApiEvent,
} from "../../redux/slices/allEventslistSlice";
import { useAppDispatch } from "../../helpers/hooks";
import { fetchEventSlots } from "../../redux/slices/eventSlotsSlice";
import { RegisterBulkVolunteers } from "../../redux/slices/bulkRegistration";
import { registerForEvent } from "../eventDetails/eventRegistrationSlice";
import RetryIcon from "../../static/images/retry_icon.svg";
import SuccessIcon from "../../static/images/success-icon-outlined.svg";
import FailedIcon from "../../static/images/failed_icon.svg";
import CustomPagination from "../common/CustomPagination";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";
import { convertDate, convertTimeTo12h } from "../../helpers/constant";
import { EventSlotState } from "../../pages/eventDetails";
import { IAllEvents } from "./volunteersLists";
interface Props {
  allUserData: any;
  onValidateShortId?: (shortId: string) => Promise<string>;
  onValidateEvent?: (eventId: string) => Promise<string>;
  onRegister?: (payload: {
    shortId: string;
    eventId?: string;
    slotId?: string;
  }) => Promise<void>;
  onToast: (type: "success" | "error" | "info", message: string) => void;
}

export interface EventOption {
  eventId: string;
  eventName: string;
}
export interface SlotOption {
  [slotId: string]: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface IUpdatedUser {
  userId: number;
  hours: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pb: 1 }}>{children}</Box>}
    </div>
  );
}

const ITEMS_PER_PAGE = 5;

const RegisterBulkVolunteer: React.FC<Props> = ({
  onValidateShortId,
  onValidateEvent,
  onRegister,
  allUserData,
  onToast,
}) => {
  const [shortId, setShortId] = useState("");
  const [selectedEvent, setSelectedEvent] = useState<string>("");
  const [selectedEventName, setSelectedEventName] = useState<string>("");
  const [selectedSlot, setSelectedSlot] = useState<string>("");

  const [validatedUser, setValidatedUser] = useState<string>();
  const [volunteers, setVolunteers] = useState(bulkVolunteerMockData);
  const [loading, setLoading] = useState(false);
  const [selectedRegisteredUsers, setSelectedRegisteredUsers] = useState<
    BulkVolunteer[]
  >([]);
  const [value, setValue] = React.useState(0);
  const [userData, setUserData] = useState<BulkVolunteer[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSlotsLoading, setIsSlotsLoading] = useState(false);
  const [pageNo, setPageNo] = React.useState(1);
  const dispatch = useAppDispatch();
  const [allEvents, setAllEvents] = useState<IAllEvents | null>(null);
  const [eventAllSlots, setEventAllSlots] = useState<MenuOption[]>([]);
  const [slots, setSlots] = useState<EventSlotState[]>([]);
  const [slotMessage, setSlotMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const formatDate = (date: any) => {
    let istDate = dayjs(date).add(330, "minute");
    let datestring = dayjs(istDate, "YYYY-MM-DD HH:mm:ss").format(
      "DD MMM, YYYY hh:mm a",
    );
    return datestring;
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const raw = (await dispatch(
          fetchAllEventsList("all"),
        ).unwrap()) as AllEventsResponse | AllEventsApiEvent[];

        const toEventOption = (event: AllEventsApiEvent | EventOption) => ({
          eventId: String(event.eventId),
          eventName: event.eventName,
        });

        const data: IAllEvents = Array.isArray(raw)
          ? { active: raw.map(toEventOption), completed: [] }
          : {
              active: raw.active?.map(toEventOption) ?? [],
              completed: raw.completed?.map(toEventOption) ?? [],
            };

        setAllEvents(data);
      } catch (error) {
        console.error("Error fetching events", error);
      }
    };
    fetchEvents();
  }, []);

  const allEventOptions = useMemo((): OptionGroup[] => {
      const groups: OptionGroup[] = [];
  
      if (allEvents?.active?.length) {
        groups.push({
          header: "Active Events",
          options: allEvents.active.map((event) => ({
            label: event.eventName,
            value: String(event.eventId),
          })),
        });
      }
  
      if (allEvents?.completed?.length) {
        groups.push({
          header: "Completed Events",
          options: allEvents.completed.map((event) => ({
            label: event.eventName,
            value: String(event.eventId),
          })),
        });
      }
  
      return groups;
    }, [allEvents]);

  const allEventList = useMemo(() => {
    return [
      ...(allEvents?.active ?? []),
      ...(allEvents?.completed ?? []),
    ];
  }, [allEvents]);

  const allSlotsOptions = useMemo(() => {
    const allSlots = slots || [];

    const grouped = allSlots.reduce((acc: any, slot: EventSlotState) => {
      const date = convertDate(slot.eventSlotStartDate);

      if (!acc[date]) acc[date] = [];

      acc[date].push({
        label: `${convertTimeTo12h(slot.eventSlotStartTime)} to ${convertTimeTo12h(slot.eventSlotEndTime)}`,
        value: String(slot.slotId),
      });

      return acc;
    }, {});

    return Object.entries(grouped).map(([date, options]) => ({
      header: date,
      options: options as MenuOption[],
    }));
  }, [slots]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allUsers = userData || [];
      setSelectedRegisteredUsers(allUsers);
    } else {
      setSelectedRegisteredUsers([]);
    }
  };

  const handleRowSelect = (email: string) => {
    const user = userData?.find((u: any) => u.email === email);
    if (!user) return;

    setSelectedRegisteredUsers((prev) => {
      const exists = prev.some((u: any) => u.email === email);
      return exists
        ? prev.filter((u: any) => u.email !== email)
        : [...prev, user];
    });
  };

  const paginatedVolunteers = userData?.slice(
    (pageNo - 1) * ITEMS_PER_PAGE,
    pageNo * ITEMS_PER_PAGE,
  );

  const isAllSelected =
    (userData?.length ?? 0) > 0 &&
    selectedRegisteredUsers.length === userData?.length;

  const handleSearch = async () => {
    if (!shortId) return;

    try {
      setLoading(true);
      const result = await onValidateShortId?.(shortId);
      setValidatedUser(result);
    } finally {
      setLoading(false);
    }
  };

  const handleRetryMultiple = async () => {
    if (!selectedEvent) {
      onToast("error", "Please select an event");
      return;
    }

    if (!selectedSlot) {
      onToast("error", "Please select a slot");
      return;
    }

    if (!selectedRegisteredUsers?.length) {
      onToast("error", "Please select users");
      return;
    }

    try {
      setLoading(true);

      const results = await Promise.allSettled(
        selectedRegisteredUsers.map((volunteer: BulkVolunteer) => {
          const payload = {
            eventId: selectedEvent,
            eventName: selectedEventName,
            mailSent: false,
            waitlisted: false,
            deletedAt: "",
            userEmail: "",
            userId: volunteer.userId,
            shortId: shortId,
            eventSlotId: selectedSlot,
            transport: volunteer.transport?.toLowerCase() === "yes",
            location: volunteer.location,
          };

          return dispatch(registerForEvent(payload));
        }),
      );

      const successCount = results.filter(
        (r) => r.status === "fulfilled",
      ).length;

      const failedCount = results.length - successCount;

      onToast("success", `${successCount} registered, ${failedCount} failed`);

      // optional cleanup
      setSelectedRegisteredUsers([]);
    } catch {
      onToast("error", "Batch retry failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEventChange = async (eventId: string) => {
    if (!eventId) return;

    setSelectedEvent(eventId);
    setIsSlotsLoading(true);

    const event = allEventList.find(
      (event) => String(event.eventId) === eventId,
    );
    setSelectedEventName(event?.eventName ?? "");

    // Reset dependent state
    setSlots([]);
    setSelectedSlot("");
    setSelectedFile(null);
    setUserData(null);
    setSlotMessage("");
    setPageNo(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    try {
      const res = await dispatch(fetchEventSlots(eventId)).unwrap();
      if (Array.isArray(res) && res.length > 0) {
        setSlots(res);
        setSlotMessage("");
      } else {
        setSlots([]);
        setSlotMessage("Slots are not available");
      }
    } catch (err) {
      console.error(err);
      setSlotMessage("Slots are not available");
    } finally {
      setIsSlotsLoading(false);
    }
  };

  const handleImportVolunteers = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const validTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (!validTypes.includes(file.type)) {
      const message = "Please upload a valid Excel file (.xls or .xlsx)";
      setFileError(message);
      onToast("error", message);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setFileError("");
    setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFilter = async () => {};

  async function handleRetryRegistration(volunteer: BulkVolunteer) {
    if (selectedEvent === "") {
      onToast("error", "Please select an event");
      return;
    }

    if (slots.length > 0 && selectedSlot === "") {
      onToast("error", "Please select a slot");
      return;
    }

    const payload = {
      eventId: selectedEvent,
      eventName: selectedEventName,
      mailSent: false,
      waitlisted: false,
      deletedAt: "",
      userEmail: "",
      userId: volunteer.userId,
      shortId: shortId,
      eventSlotId: selectedSlot,
      transport: volunteer.transport?.toLowerCase() === "yes",
      location: volunteer.location,
    };

    try {
      setLoading(true);
      const registerRes = await dispatch(registerForEvent(payload));

      if (registerRes.meta.requestStatus === "fulfilled") {
        onToast("success", "Volunteer registered successfully!");
      } else {
        const errorMessage: string =
          registerRes.payload?.response?.data || "Registration failed.";
        onToast("error", errorMessage);
      }
    } catch (error) {
      onToast("error", "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  }

  const handleRegisterVolunteers = async () => {
    if (!selectedFile) {
      setFileError("Please select a file first");
      return;
    }

    if (slots.length > 0 && selectedSlot === "") {
      onToast("error", "Please select a slot");
      return;
    }

    setIsLoading(true);

    try {
      const eventParam = {
        eventId: selectedEvent,
        slotId: selectedSlot,
      };

      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("bulkRegistrationRequest", JSON.stringify(eventParam));

      const response = await dispatch(
        RegisterBulkVolunteers(formData),
      ).unwrap();

      setVolunteers(response as BulkVolunteer[]);
      setUserData(response as BulkVolunteer[]);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      onToast("success", "Updated Successfully!");
    } catch (error) {
      console.error(error);
      onToast("error", "Something went wrong!");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSlotChange = (slot: string) => {
    setSelectedSlot(slot);
    setPageNo(1);
  };

  return (
    <Box>
      <Grid
        container
        spacing={3}
        alignItems="center"
        sx={{ paddingLeft: 4, paddingRight: 4, paddingBottom: 2 }}
      >
        <Grid size={{ xs: 12, md: 2.5 }}>
          <CommonSelect
            placeholder="Select Event"
            groupedOptions={allEventOptions}
            searchable
            showSectionHeader            
            value={selectedEvent}
            onChangeHandler={handleEventChange}
            disabled={false}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 2.5 }}>
          <CommonSelect
            placeholder={
              slotMessage ? "Slots are not available" : "Select Time Slot"
            }
            value={selectedSlot}
            showSectionHeader
            options={[]}
            groupedOptions={allSlotsOptions}
            onChangeHandler={handleSlotChange}
            disabled={selectedEvent ? false : true}
            loading={isSlotsLoading}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {/* <Grid size={{ xs: 12, md: 3 }}> */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xls,.xlsx"
              style={{ display: "none" }}
              id="excel-upload"
              onChange={handleImportVolunteers}
              disabled={
                !selectedEvent ||
                isSlotsLoading ||
                (slots.length > 0 && selectedSlot === "")
              }
            />

            <label
              htmlFor="excel-upload"
              title="Only .xls or .xlsx files are supported"
            >
              <LoadingButton
                sx={{
                  height: 56,
                  minWidth: 110,
                  gap: 1,
                  textTransform: "none",
                  "&.Mui-disabled": {
                    backgroundColor: "#bdbdbd",
                    color: "#757575",
                  },
                }}
                variant="contained"
                component="span"
                disabled={
                  !selectedEvent ||
                  isSlotsLoading ||
                  (slots.length > 0 && selectedSlot === "")
                }
              >
                <Avatar
                  sx={{ height: 18, width: 18 }}
                  src={ImportIcon}
                />
                Import
              </LoadingButton>
            </label>

            {selectedFile && (
              <Box
                sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}
              >
                <Typography
                  sx={{
                    minWidth: 120,
                    // fixed width
                    // whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  variant="body2"
                >
                  {selectedFile.name}
                </Typography>

                <button
                  onClick={handleRemoveFile}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    marginLeft: 8,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                  title="remove"
                >
                  <CloseIcon sx={{ color: "red", fontSize: 18 }} />
                </button>
              </Box>
            )}

            {fileError && (
              <Typography color="error" variant="caption">
                {fileError}
              </Typography>
            )}
            {/* </Grid> */}

            <LoadingButton
              loading={isLoading}
              loadingIndicator="Registering…"
              sx={{
                height: 56,
                minWidth: 130,
                textTransform: "none",
                "&.Mui-disabled": {
                  backgroundColor: "#bdbdbd",
                  color: "#757575",
                },
              }}
              variant="contained"
              disabled={
                !(selectedEvent && selectedFile && !isSlotsLoading &&
                  (slots.length === 0 || selectedSlot !== ""))
              }
              onClick={handleRegisterVolunteers}
            >
              Register
            </LoadingButton>
          </Box>
        </Grid>
        {/* <Grid
          sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}
          size={{ xs: 12, md: 4 }}
        >
          <Button
            variant="contained"
            onClick={handleFilter}
            sx={{
              backgroundColor: "#1976d2",
              textTransform: "none",
              fontWeight: 600,
              height: 56,
              gap: 1,
            }}
          >
            Filter
            <Avatar sx={{ height: 18, width: 18 }} src={FilterIcon} />
          </Button>
        </Grid> */}
      </Grid>
      <ComponentDivider text="" my={0} />
      <Box sx={{ width: "100%" }}>
        <CustomTabPanel value={value} index={0}>
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                <TableRow>
                  {/* <TableCell padding="checkbox">
                    <Box display="flex" alignItems="center">
                      <Checkbox
                        indeterminate={
                          selectedRegisteredUsers.length > 0 &&
                          selectedRegisteredUsers.length <
                            (userData?.length ?? 0)
                        }
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                      />
                      <Typography fontWeight={600}>All</Typography>
                    </Box>
                  </TableCell> */}
                  <TableCell>
                    <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                      Name
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                      Dept.
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                      Date
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                      Transport
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                      Location
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                      Status
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                      Action
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody sx={{ backgroundColor: "#fff" }}>
                {(paginatedVolunteers?.length ?? 0) === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Box
                        py={6}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: "text.secondary" }}
                        >
                          No users to display
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1,
                            color: "text.secondary",
                            maxWidth: 400,
                            lineHeight: 1.6,
                          }}
                        >
                          Select an{" "}
                          <Box component="span" sx={{ fontWeight: 500 }}>
                            event
                          </Box>{" "}
                          and{" "}
                          <Box component="span" sx={{ fontWeight: 500 }}>
                            slot
                          </Box>
                          , then upload an Excel file to view users here.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedVolunteers?.map((listItem: BulkVolunteer) => (
                    <TableRow key={listItem.shortId}>
                      {/* <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRegisteredUsers.some(
                            (u: any) => u.email === listItem.email,
                          )}
                          onChange={() => handleRowSelect(listItem.email)}
                        />
                      </TableCell> */}
                      <TableCell>{listItem.name}</TableCell>
                      <TableCell>{listItem.dept}</TableCell>
                      <TableCell>{formatDate(listItem.date)}</TableCell>
                      <TableCell>
                        {listItem.transport?.toLowerCase() === "yes"
                          ? "Yes"
                          : "No"}
                      </TableCell>
                      <TableCell>{listItem.location}</TableCell>
                      <TableCell
                        sx={{
                          color:
                            listItem.registeredStatus === "Yes"
                              ? "green"
                              : "red",
                          fontWeight: 600,
                        }}
                      >
                        {listItem.registeredStatus === "Yes" ? (
                          <Avatar
                            sx={{
                              width: "30%",
                              height: "30%",
                            }}
                            src={SuccessIcon}
                          />
                        ) : (
                          <Avatar
                            sx={{
                              width: "25%",
                              height: "25%",
                              alignSelf: "center",
                            }}
                            src={FailedIcon}
                            title="registration failed: The User may be invalid or already registered."
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {listItem.registeredStatus === "Yes" ? null : (
                          <Box sx={{ display: "flex", gap: 2 }}>
                            <Button
                              variant="text"
                              sx={{
                                borderRadius: "2px",
                                borderWidth: 1,
                                fontWeight: 700,
                                textTransform: "none",
                              }}
                              onClick={() => handleRetryRegistration(listItem)}
                              disabled={slots.length > 0 && selectedSlot === ""}
                              title="retry"
                            >
                              <img
                                height={24}
                                width={24}
                                src={RetryIcon}
                                alt="retry icon"
                              />
                            </Button>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ACTION BUTTONS */}
          {(userData?.length ?? 0) > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                mb: 1,
                height: "48px",
              }}
            >
              {/* <Box sx={{ flex: 1 }} /> */}

              {/* <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  sx={{
                    borderRadius: "2px",
                    borderWidth: 1,
                    fontWeight: 700,
                    textTransform: "none",
                  }}
                  onClick={handleRetryMultiple}
                  disabled={selectedRegisteredUsers.length === 0}
                >
                  Try again
                </Button>
              </Box> */}

              <Box
                sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}
              >
                <CustomPagination
                  totalItems={userData?.length ?? 0}
                  currentPage={pageNo}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={setPageNo}
                />
              </Box>
            </Box>
          )}
        </CustomTabPanel>
      </Box>
    </Box>
  );
};

export default RegisterBulkVolunteer;
